<template>
  <v-card class="conversation-section" v-if="bookingId">
    <v-card-title class="conversation-header">
      <span>{{ $t("conversation.title") }}</span>
      <v-spacer></v-spacer>
      <v-btn
        small
        color="primary"
        :loading="exporting"
        :disabled="!messages.length"
        @click="exportConversation"
      >
        <v-icon small left>file_download</v-icon>
        {{ $t("conversation.export") }}
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-tabs v-model="tab" color="#FAFAFA" slider-color="primary" fixed-tabs>
        <v-tab ripple>{{ $t("conversation.title") }}</v-tab>
        <v-tab ripple>{{ $t("conversation.callLog") }}</v-tab>

        <!-- Conversation transcript -->
        <v-tab-item>
          <div class="messages" v-if="messages.length">
            <div
              v-for="m in messages"
              :key="m.id"
              class="msg-row"
              :class="{ mine: m.sender_type === 'captain' }"
            >
              <div class="bubble" :class="m.sender_type">
                <div class="meta">
                  <strong>{{ senderName(m) }}</strong>
                  <span class="time">{{ formatTime(m.created_at) }}</span>
                </div>
                <div class="body" v-if="m.type === 'voice'">
                  <v-btn small flat class="voice-btn" :loading="playingId === m.id && voiceLoading" @click="playVoice(m)">
                    <v-icon small class="mr-1">{{ playingId === m.id ? "pause" : "play_arrow" }}</v-icon>
                    <v-icon small class="mr-1">mic</v-icon>
                    {{ formatDuration(m.duration_seconds) }}
                  </v-btn>
                </div>
                <div class="body" v-else>{{ m.body }}</div>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-4 grey--text">
            {{ $t("conversation.noMessages") }}
          </div>
        </v-tab-item>

        <!-- Call log -->
        <v-tab-item>
          <div v-if="callLogs.length" class="call-log">
            <table class="call-table">
              <thead>
                <tr>
                  <th>{{ $t("conversation.caller") }}</th>
                  <th>{{ $t("conversation.callee") }}</th>
                  <th>{{ $t("conversation.status") }}</th>
                  <th>{{ $t("conversation.duration") }}</th>
                  <th>{{ $t("conversation.startedAt") }}</th>
                  <th>{{ $t("conversation.endedAt") }}</th>
                  <th>{{ $t("conversation.failReason") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in callLogs" :key="c.id">
                  <td>{{ c.caller_name || partyLabel(c.caller_type) }}</td>
                  <td>{{ c.callee_name || partyLabel(c.callee_type) }}</td>
                  <td>{{ c.status }}</td>
                  <td>{{ formatDuration(c.duration_seconds) }}</td>
                  <td>{{ formatDateTime(c.started_at) }}</td>
                  <td>{{ formatDateTime(c.ended_at) }}</td>
                  <td>{{ c.fail_reason || "—" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-center py-4 grey--text">
            {{ $t("conversation.noCalls") }}
          </div>
        </v-tab-item>
      </v-tabs>
    </v-card-text>
  </v-card>
</template>

<script>
/* eslint-disable */
import axios from "axios";
import api from "@/constants/api";
import { formatCompanyTime } from "@/util/companyTime";
import { saveAs } from "file-saver";

export default {
  name: "RideConversation",
  props: {
    bookingId: { type: Number, required: true },
  },
  data() {
    return {
      tab: 0,
      messages: [],
      callLogs: [],
      exporting: false,
      audio: null,
      playingId: null,
      voiceLoading: false,
    };
  },
  mounted() {
    this.fetchChat();
    this.fetchCallLogs();
  },
  beforeDestroy() {
    if (this.audio) this.audio.pause();
  },
  methods: {
    playVoice(m) {
      if (this.playingId === m.id && this.audio) {
        this.audio.pause();
        this.playingId = null;
        return;
      }
      if (this.audio) this.audio.pause();
      this.voiceLoading = true;
      this.playingId = m.id;
      axios
        .get(m.voice_url, { responseType: "blob" })
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          this.audio = new Audio(url);
          this.audio.onended = () => {
            this.playingId = null;
          };
          this.audio.play();
        })
        .catch(() => {
          this.playingId = null;
        })
        .finally(() => {
          this.voiceLoading = false;
        });
    },
    fetchChat() {
      axios
        .get(`${api.ACTIONS.BOOKINGS}/${this.bookingId}/chat`)
        .then((res) => {
          const data = (res.data && res.data.data) || {};
          this.messages = data.messages || [];
        })
        .catch(() => {});
    },
    fetchCallLogs() {
      axios
        .get(`${api.ACTIONS.BOOKINGS}/${this.bookingId}/call-logs`)
        .then((res) => {
          const data = (res.data && res.data.data) || {};
          this.callLogs = data.call_logs || [];
        })
        .catch(() => {});
    },
    exportConversation() {
      this.exporting = true;
      axios
        .get(`${api.ACTIONS.BOOKINGS}/${this.bookingId}/chat/export`, {
          responseType: "blob",
        })
        .then((res) => {
          saveAs(res.data, `conversation-booking-${this.bookingId}.csv`);
        })
        .catch(() => {})
        .finally(() => {
          this.exporting = false;
        });
    },
    senderName(m) {
      if (m.sender_name) return m.sender_name;
      return this.partyLabel(m.sender_type);
    },
    partyLabel(type) {
      return type === "passenger"
        ? this.$t("conversation.passenger")
        : this.$t("conversation.captain");
    },
    formatTime(dt) {
      // Final-version doc 2026-10-05: company clock (UTC+3), not the PC's zone.
      return formatCompanyTime(dt, "HH:mm", "");
    },
    formatDateTime(dt) {
      // Compact (no redundant year) so the column stays on one line.
      return formatCompanyTime(dt, "MM-DD HH:mm", "—");
    },
    formatDuration(s) {
      if (!s) return "0:00";
      const m = Math.floor(s / 60);
      const sec = s % 60;
      return `${m}:${sec.toString().padStart(2, "0")}`;
    },
  },
};
</script>

<style scoped>
.conversation-section {
  margin-top: 16px;
}
.conversation-header {
  font-weight: 600;
}
.messages {
  display: flex;
  flex-direction: column;
  padding: 8px 4px;
  max-height: 480px;
  overflow-y: auto;
}
.msg-row {
  display: flex;
  justify-content: flex-start;
  margin: 4px 0;
}
.msg-row.mine {
  justify-content: flex-end;
}
.bubble {
  max-width: 75%;
  padding: 8px 12px;
  border-radius: 12px;
  background: #f1f1f1;
}
.bubble.captain {
  background: #e7f6e5;
}
.bubble .meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 11px;
  color: #737780;
  margin-bottom: 2px;
}
.bubble .time {
  white-space: nowrap;
}
.bubble .body {
  font-size: 14px;
  color: #241f20;
  white-space: pre-wrap;
  word-break: break-word;
}
.call-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.call-table th,
.call-table td {
  text-align: start;
  padding: 6px 8px;
  border-bottom: 1px solid #eee;
  white-space: nowrap; /* keep dates/values on a single line */
}
.call-table th {
  color: #737780;
  font-weight: 600;
}
</style>
